import { Link } from "react-router-dom";
import clsx from "clsx";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "@/assets/img/logo_inline.svg?react";
import { isNavigationItemActive } from "@/components/Layout";
import { NavigationSidebarProps } from "./navigationSidebar.types";

export function NavigationSidebar({
  navigation,
  currentPath,
  onNavigate,
  onClose,
  isMobile = false,
  isCollapsed = false,
}: NavigationSidebarProps) {
  const handleNavigation = () => {
    onNavigate?.();
    if (isMobile) {
      onClose?.();
    }
  };

  const showCollapsed = !isMobile && isCollapsed;

  return (
    <div className={clsx(
      "flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-primary-200 pb-4 shadow-raro transition-all duration-300",
      showCollapsed ? "px-2" : "px-6"
    )}>
      {/* Header - Logo + Close button (mobile) */}
      {isMobile && (
        <div className="flex h-16 shrink-0 items-center justify-between">
          <Link
            to="/"
            onClick={handleNavigation}
            className="text-2xl font-bold text-raro-gray-900 font-montserrat hover:text-primary-600 transition-colors"
          >
            <Logo className="h-8 w-auto" />
          </Link>

          {/* Close button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-raro-gray-400 hover:text-raro-gray-600 transition-colors"
            onClick={onClose}
          >
            <span className="sr-only">Fechar sidebar</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            {!showCollapsed && (
              <div
                className={clsx(
                  "text-xs font-semibold leading-6 text-raro-gray-400 font-montserrat uppercase tracking-wider",
                  isMobile ? "mt-0" : "mt-8"
                )}
              >
                Navegação Principal
              </div>
            )}
            <ul role="list" className={clsx(
              "space-y-1 mt-4",
              showCollapsed ? "mx-0" : "-mx-2"
            )}>
              {navigation.map((item) => {
                const isActive = isNavigationItemActive(item.href, currentPath);

                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={handleNavigation}
                      className={clsx(
                        "group flex items-center rounded-xl text-sm leading-6 font-montserrat font-medium transition-all duration-200",
                        showCollapsed ? "p-2 justify-center" : "gap-x-3 p-3",
                        isActive
                          ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-raro"
                          : "text-raro-gray-700 hover:text-primary-600 hover:bg-primary-50 hover:shadow-sm"
                      )}
                      title={showCollapsed ? item.name : undefined}
                    >
                      <item.icon
                        className={clsx(
                          "h-5 w-5 shrink-0 transition-colors",
                          isActive
                            ? "text-white"
                            : "text-raro-gray-400 group-hover:text-primary-600"
                        )}
                        aria-hidden="true"
                      />

                      {!showCollapsed && (
                        <>
                          <span className="truncate">{item.name}</span>

                          {/* Active indicator */}
                          {isActive && (
                            <div className="ml-auto">
                              <div className="h-2 w-2 rounded-full bg-white opacity-60" />
                            </div>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Bottom section - pode ser usado para informações adicionais */}
          <li className="mt-auto">
            <div className={clsx(
              "rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 transition-all duration-300",
              showCollapsed ? "p-2" : "p-4"
            )}>
              <div className={clsx(
                "flex items-center",
                showCollapsed && "justify-center"
              )}>
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="text-xs font-bold text-white font-montserrat">
                      R
                    </span>
                  </div>
                </div>
                {!showCollapsed && (
                  <div className="ml-3">
                    <p className="text-sm font-medium text-raro-gray-900 font-montserrat">
                      radar.digital
                    </p>
                    <p className="text-xs text-raro-gray-500 font-montserrat">
                      Sistema de Gestão
                    </p>
                  </div>
                )}
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}

export default NavigationSidebar;
